import { useState, useEffect, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import {
  fetchProviderIdWithEntra,
  fetchGroupId,
  fetchGroupPlans,
  fetchProviderOrganization,
} from '../helpers/apiHelpers';
import { ACTION_TYPES } from '../constants/formConstants';
import useAuth from './useAuth';

/**
 * Custom hook for fetching plans and provider/group data
 * Handles the chain of API calls needed to load available plans
 */
const usePlans = () => {
  const { isAuthenticated, acquireToken } = useAuth();

  // Plan and provider data
  const [plans, setPlans] = useState([]);
  const [planIdMap, setPlanIdMap] = useState([]);
  const [groupIdMap, setGroupIdMap] = useState(null);
  const [organizationId, setOrganizationId] = useState(null);
  const [providerId, setProviderId] = useState('');

  // Available action types (based on group permissions)
  const [availableActionTypes, setAvailableActionTypes] = useState([
    ACTION_TYPES.CREATE_CASE,
    ACTION_TYPES.DEBUG_INFO,
  ]);

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Determines if the current group can create cases
   * @param {string} groupId - The group ID to check
   * @returns {boolean}
   */
  const canCreateCases = useCallback((groupId) => {
    const allowedGroups = process.env.REACT_APP_ALLOWED_GROUPS_TO_CREATE_CASES;
    return allowedGroups === '*' || groupId === allowedGroups;
  }, []);

  /**
   * Fetches all plan and provider data
   * Chain: token → sub → providerId → groupId/orgId → plans
   */
  const fetchPlans = useCallback(async () => {
    if (!isAuthenticated) {
      console.log('Waiting for MSAL authentication...');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Get access token
      const accessToken = await acquireToken();

      // Decode token to get user sub
      const decoded = jwtDecode(accessToken);
      const sub = decoded.sub;

      // Fetch provider ID from Entra
      const fetchedProviderId = await fetchProviderIdWithEntra(sub, accessToken);
      setProviderId(fetchedProviderId);

      // Fetch group and organization data in parallel
      const [fetchedGroupIdMap, orgMap] = await Promise.all([
        fetchGroupId(fetchedProviderId, accessToken),
        fetchProviderOrganization(fetchedProviderId, accessToken),
      ]);

      setGroupIdMap(fetchedGroupIdMap);
      setOrganizationId(orgMap.id);
      console.log('Organization ID:', orgMap.id);

      // Fetch available plans for the group
      const fetchedPlanIdMap = await fetchGroupPlans(
        fetchedGroupIdMap.id,
        orgMap.id,
        fetchedProviderId,
        accessToken
      );
      setPlanIdMap(fetchedPlanIdMap);

      // Extract plan names for dropdown
      const newAvailablePlans = fetchedPlanIdMap.map((plan) => plan.name);
      setPlans(newAvailablePlans);

      // Determine available action types based on group permissions
      console.log('Allowed Groups:', process.env.REACT_APP_ALLOWED_GROUPS_TO_CREATE_CASES);
      if (canCreateCases(fetchedGroupIdMap.id)) {
        setAvailableActionTypes([ACTION_TYPES.CREATE_CASE, ACTION_TYPES.DEBUG_INFO]);
      } else {
        setAvailableActionTypes([ACTION_TYPES.DEBUG_INFO]);
      }
    } catch (err) {
      console.error('Error fetching plans:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, acquireToken, canCreateCases]);

  // Fetch plans on mount when authenticated
  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  /**
   * Find plan details by plan name
   * @param {string} planName - Name of the plan to find
   * @returns {object|null} Plan details or null if not found
   */
  const getPlanByName = useCallback(
    (planName) => {
      return planIdMap.find((plan) => plan.name === planName) || null;
    },
    [planIdMap]
  );

  /**
   * Check if user can create cases (based on group permissions)
   */
  const hasCreateCasePermission = availableActionTypes.includes(ACTION_TYPES.CREATE_CASE);

  return {
    // Data
    plans,
    planIdMap,
    groupIdMap,
    organizationId,
    providerId,
    availableActionTypes,

    // State
    isLoading,
    error,

    // Helpers
    getPlanByName,
    hasCreateCasePermission,

    // Actions
    refetchPlans: fetchPlans,
  };
};

export default usePlans;
